/**
 * `<LoginRoute>` — `/login`
 *
 * Per `aidlc-docs/construction/auth-redesign/functional-design/frontend-components.md § 2`
 * (ENG-35588), updated for the cookies-removed auth-redesign.
 *
 * Two authentication paths:
 *   1. Google OAuth — full-page navigation; the API handles the WorkOS
 *      dance and 302-redirects to `/auth/handoff#token=<jwt>&next=...`
 *      where {@link AuthHandoffRoute} persists the token to
 *      `localStorage` and navigates onward.
 *   2. Email + password — POST /api/auth/login/password. On success the
 *      `authClient.loginWithPassword` helper captures the returned
 *      `token` into `localStorage` for us; the response body also carries
 *      the user's most-recently-accessed workspace (or `null` for a
 *      workspace-less session). The dashboard navigates to `/w/<slug>`
 *      or `/no-workspace` accordingly.
 *
 * Magic-link was removed from the surface area (FR-1); the underlying
 * `authClient.requestMagicLink` is preserved (commented) in the client
 * for if/when the product re-enables it. See TODO(magic-link) markers in
 * `packages/auth/src/server/nestjs/controllers/auth.controller.ts` for
 * the corresponding backend posture.
 *
 * The visual treatment follows the design-system "Dashboard/Auth/Sign In"
 * fixture: title + description in the card header (owned by <AuthLayout>),
 * email/password first, then a Google SSO fallback below an "or" divider.
 */

import { authClient, useAuth, type AuthClientError } from "@cometchat-ai/auth/client";
import { loginPasswordSchema, type LoginPasswordDto } from "@cometchat-ai/auth/shared";
import {
  Button,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Input,
  Separator,
  toast,
} from "@cometchat-ai/ui-dashboard";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Eye, EyeOff, Loader2 } from "../components/icons";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useSearchParams } from "react-router";

import { AuthLayout } from "../layouts/auth-layout";
import { formatApiError } from "../lib/format-error";

/**
 * Inline Google "G" mark. Avoids pulling a brand-icon dependency just for
 * one button and keeps the bundle slim. Render small (h-4) — the colors
 * are the official Google brand palette (a fixed brand asset, intentionally
 * exempt from the design-token rule).
 */
function GoogleIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 48 48" aria-hidden="true" className={className}>
      <path
        fill="#FFC107"
        d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-1.341-.138-2.65-.389-3.917z"
      />
      <path
        fill="#FF3D00"
        d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571.001-.001.002-.001.003-.002l6.19 5.238C36.971 39.205 44 34 44 24c0-1.341-.138-2.65-.389-3.917z"
      />
    </svg>
  );
}

export function LoginRoute() {
  const { t } = useTranslation("auth");
  const navigate = useNavigate();
  const auth = useAuth();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") ?? "/";
  const [showPassword, setShowPassword] = useState(false);

  const passwordForm = useForm<LoginPasswordDto>({
    resolver: zodResolver(loginPasswordSchema),
    defaultValues: { email: "", password: "" },
  });

  const passwordMutation = useMutation({
    mutationFn: (values: LoginPasswordDto) => authClient.loginWithPassword(values),
    onSuccess: async (result) => {
      // Re-fetch /api/me so <RequireAuth> sees the new session immediately
      // and React Query's cache flips from anon → authenticated atomically.
      await auth.refresh();
      // Decide landing: if the API returned a workspace, go there (or
      // honor `?redirect=` when it points to a /w/ URL); otherwise the
      // user is workspace-less → onboarding.
      if (result.workspace) {
        navigate(redirect.startsWith("/w/") ? redirect : `/w/${result.workspace.slug}`);
      } else {
        navigate("/no-workspace");
      }
    },
    onError: (err: AuthClientError) => {
      toast.error(t("login.failed"), { description: formatApiError(err) });
    },
  });

  return (
    <AuthLayout title={t("login.title")} subtitle={t("login.description")}>
      <div className="space-y-4">
        <Form {...passwordForm}>
          <form
            onSubmit={passwordForm.handleSubmit((values) => passwordMutation.mutate(values))}
            className="space-y-4"
            data-testid="login-password-form"
            noValidate
          >
            <FormField
              control={passwordForm.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("login.emailLabel")}</FormLabel>
                  <FormControl>
                    <Input
                      type="email"
                      autoComplete="email"
                      inputMode="email"
                      placeholder={t("login.emailPlaceholder")}
                      data-testid="login-password-email"
                      className="h-12 rounded-xl text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={passwordForm.control}
              name="password"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t("login.passwordLabel")}</FormLabel>
                  <FormControl>
                    <div className="relative">
                      <Input
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        placeholder={t("login.passwordPlaceholder")}
                        data-testid="login-password"
                        className="h-12 rounded-xl pr-10 text-sm placeholder:text-[color:var(--color-fg-subtle)]"
                        {...field}
                      />
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => setShowPassword((v) => !v)}
                        aria-label={
                          showPassword ? t("login.hidePassword") : t("login.showPassword")
                        }
                        aria-pressed={showPassword}
                        className="absolute right-1 top-1/2 h-8 w-8 -translate-y-1/2 text-muted-foreground"
                      >
                        {showPassword ? <EyeOff aria-hidden="true" /> : <Eye aria-hidden="true" />}
                      </Button>
                    </div>
                  </FormControl>
                  <FormMessage />
                  <div className="mt-2 flex justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-muted-foreground underline-offset-4 hover:text-foreground hover:underline"
                    >
                      {t("login.forgotPassword")}
                    </Link>
                  </div>
                </FormItem>
              )}
            />
            <Button
              type="submit"
              className="h-10 w-full rounded-xl hover:bg-primary-hover"
              disabled={passwordMutation.isPending}
              data-testid="login-password-submit"
            >
              {passwordMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />
              )}
              {passwordMutation.isPending ? t("login.submitting") : t("login.submit")}
            </Button>
          </form>
        </Form>

        <div className="flex items-center gap-3">
          <Separator className="flex-1" />
          <span className="text-xs uppercase text-muted-foreground">{t("login.or")}</span>
          <Separator className="flex-1" />
        </div>

        <Button
          type="button"
          variant="outline"
          className="h-12 w-full rounded-xl"
          onClick={() => authClient.beginGoogleLogin()}
          data-testid="login-google"
        >
          <GoogleIcon className="mr-2 h-4 w-4" />
          {t("login.googleButton")}
        </Button>

        <p className="text-center text-sm text-muted-foreground">
          {t("login.newToProduct")}{" "}
          <Link
            to="/signup"
            className="font-medium text-primary underline-offset-4 hover:underline"
          >
            {t("login.createAccount")}
          </Link>
        </p>
      </div>
    </AuthLayout>
  );
}
